import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings2 } from 'lucide-react';

const AnalysisTypeSelectorLoading = () => (
    <div>
        <div className="space-y-4 rounded-lg bg-muted/50 p-4 mb-6">
            <div className="flex items-center gap-2 text-sm font-medium">
                <Settings2 className="h-4 w-4 text-blue-600" />
                <h3>Analysis Settings</h3>
            </div>
            <div className="grid gap-4">
                <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-5 w-10" />
                </div>
                <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-40" />
                    </div>
                    <Skeleton className="h-5 w-10" />
                </div>
            </div>
        </div>

        <div className="space-y-4">
            <div className="space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-10 w-full" />
            </div>
        </div>
    </div>
);

export default function AnalysisTypeSelector({
    isDualAnalysis,
    setIsDualAnalysis,
    selectedTypes,
    setSelectedTypes,
    analysisTypes,
    isAnalyzing,
    isLoading
}) {
    const [showCustomOnly, setShowCustomOnly] = useState(false);

    if (isLoading) {
        return <AnalysisTypeSelectorLoading />;
    }

    const filteredAnalysisTypes = showCustomOnly
        ? analysisTypes.filter(type => type.isCustom)
        : analysisTypes;

    const handleCustomFilterChange = (checked) => {
        setShowCustomOnly(checked);
        const primaryExists = filteredAnalysisTypes.some(type => type.id === selectedTypes.primary);
        const secondaryExists = filteredAnalysisTypes.some(type => type.id === selectedTypes.secondary);

        setSelectedTypes(prev => ({
            primary: primaryExists ? prev.primary : '',
            secondary: secondaryExists ? prev.secondary : ''
        }));
    };

    // Count custom analysis types
    const customTypesCount = analysisTypes.filter(type => type.isCustom).length;

    return (
        <div>
            {/* Settings Section */}
            <div className="space-y-4 rounded-lg bg-muted/50 p-4 mb-6">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Settings2 className="h-4 w-4 text-blue-600" />
                    <h3>Analysis Settings</h3>
                </div>
                <div className="grid gap-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Dual Analysis</Label>
                            <p className="text-xs text-muted-foreground">
                                Run two different analyses simultaneously
                            </p>
                        </div>
                        <Switch
                            checked={isDualAnalysis}
                            onCheckedChange={setIsDualAnalysis}
                            disabled={isAnalyzing}
                            className="data-[state=checked]:bg-blue-600"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Custom Analysis Only</Label>
                            <p className="text-xs text-muted-foreground">
                                Show only your {customTypesCount} custom analysis types
                            </p>
                        </div>
                        <Switch
                            checked={showCustomOnly}
                            onCheckedChange={handleCustomFilterChange}
                            disabled={isAnalyzing}
                            className="data-[state=checked]:bg-blue-600"
                        />
                    </div>
                </div>
            </div>

            {/* Analysis Type Selection */}
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label className="text-sm font-medium">Primary Analysis</Label>
                    <Select
                        value={selectedTypes.primary}
                        onValueChange={(value) =>
                            setSelectedTypes(prev => ({ ...prev, primary: value }))
                        }
                        disabled={isAnalyzing}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select primary analysis type" />
                        </SelectTrigger>
                        <SelectContent>
                            {filteredAnalysisTypes.map((type) => (
                                <SelectItem
                                    className="cursor-pointer"
                                    key={type.id}
                                    value={type.id}
                                >
                                    <span className="flex items-center gap-2">
                                        {type.name}
                                        {type.isCustom && (
                                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                Custom
                                            </span>
                                        )}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {isDualAnalysis && (
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Secondary Analysis</Label>
                        <Select
                            value={selectedTypes.secondary}
                            onValueChange={(value) =>
                                setSelectedTypes(prev => ({ ...prev, secondary: value }))
                            }
                            disabled={isAnalyzing}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select secondary analysis type" />
                            </SelectTrigger>
                            <SelectContent>
                                {filteredAnalysisTypes
                                    .filter(type => type.id !== selectedTypes.primary)
                                    .map((type) => (
                                        <SelectItem
                                            className="cursor-pointer"
                                            key={type.id}
                                            value={type.id}
                                        >
                                            <span className="flex items-center gap-2">
                                                {type.name}
                                                {type.isCustom && (
                                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                                        Custom
                                                    </span>
                                                )}
                                            </span>
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
        </div>
    );
}